// src/Pages/Templates/TemplatesListPage.jsx
import React, { useState, useEffect } from 'react';
import { Button, Container, Row, Col } from 'react-bootstrap';
import Table from '../../Components/Table/Table';
import { MsalAuthenticationTemplate, useMsal } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import { loginRequest } from '../../authConfig';
import { DataPullerService } from '../../Services/DataPullerService';
import { TemplateSchedulerDataService } from '../../Services/TemplateSchedulerDataService';
import TaskDetailsModal from '../TaskListPage/TaskDetailsModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlasses, faRefresh, faCalendar } from '@fortawesome/free-solid-svg-icons';
import { formatGuatemalanDateTime } from '../../Utils/dateFormat';
import { PageLayout2 } from '../../Components/PageLayout/PageLayout2';
import './TemplatesListPage.css';
import TaskSchedulerModal from '../SchedulerTaskListPage/TaskSchedulerModal';
import { useNavigate } from 'react-router-dom';

const TemplatesListPageContent = () => {
  const { instance, accounts, inProgress } = useMsal();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showSchedulerModal, setShowSchedulerModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (inProgress === 'none' && accounts.length > 0) {
      fetchTemplates();
    }
  }, [inProgress, accounts]);

  const fetchTemplates = async () => {
    try {
      const request = { ...loginRequest, account: accounts[0] };
      const { accessToken } = await instance.acquireTokenSilent(request);
      const data = await DataPullerService.getTemplates(accessToken);
      setTemplates(data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const handleSelectTemplate = async (templateId) => {
    try {
      const request = { ...loginRequest, account: accounts[0] };
      const { accessToken } = await instance.acquireTokenSilent(request);
      const data = await DataPullerService.getTemplateDetails(templateId, accessToken);
      setSelectedTemplate(data);
      setShowDetailsModal(true);
    } catch (error) {
      console.error('Failed to fetch template details:', error);
    }
  };

  const handleOpenScheduler = (templateId) => {
    const template = templates.find(x => x.templateId == templateId);
    setSelectedTemplate(template);
    setShowSchedulerModal(true);
  };

  const handleScheduleSave = async (jobData) => {
    try {
      const request = { ...loginRequest, account: accounts[0] };
      const { accessToken } = await instance.acquireTokenSilent(request);
      jobData.taskName = selectedTemplate.description;
      jobData.createdBy = accounts[0].username;
      const data = await TemplateSchedulerDataService.scheduleTemplate(
        selectedTemplate.templateId,
        jobData,
        accessToken
      );
      setShowSchedulerModal(false);
      setShowConfirmationModal(true);  // or directly fetchTemplates()
    } catch (error) {
      console.error('Failed to schedule template:', error);
      setShowErrorModal(true);
    }
  };

  const handleNavigateToNewTask = () => {
    navigate('/data-extraction/new-task');
  }

  const columns = React.useMemo(() => [
    { header: 'ID', accessorKey: 'templateId' },
    { header: 'Extraction Type', accessorKey: 'pipelineName' },
    { header: 'Name', accessorKey: 'description' },
    {
      header: 'Creation Date',
      accessorKey: 'createdDate',
      cell: info => formatGuatemalanDateTime(info.getValue()),
      meta: { filterVariant: 'date-range' },
      filterFn: 'dateBetweenFilterFn'
    },
    {
      header: 'Actions',
      id: 'actions',
      cell: ({ row }) => (
        <div className="d-flex gap-2">
          <Button
            size="sm"
            variant="outline-dark"
            onClick={() => handleSelectTemplate(row.original.templateId)}
          >
            <FontAwesomeIcon icon={faGlasses} />
          </Button>
          <Button
            size="sm"
            variant="outline-info"
            onClick={() => handleOpenScheduler(row.original.templateId)}
          >
            <FontAwesomeIcon icon={faCalendar} />
          </Button>
        </div>
      )
    }
  ], [templates]);

  return (
    <Container fluid className="h-100 d-flex flex-column TemplatesListPage">
      <div className='d-flex justify-content-end w-100'>
        <button className='btn btn-xl btn-outline-primary fw-bold me-2' onClick={fetchTemplates}>
          <FontAwesomeIcon icon={faRefresh} />
        </button>
        <button className='btn btn-xl btn-primary fw-bold' onClick={handleNavigateToNewTask}>New Task</button>
      </div>
      <Row className="my-4 templateslist-content">
        <Col sm={12} className="table-col">
          <Table
            columns={columns}
            data={templates}
            tableClass="table-hover"
            containerClass="table-responsive"
            headerClass="table-light"
          />
        </Col>
      </Row>

      {showDetailsModal && (
        <TaskDetailsModal
          show={showDetailsModal}
          onHide={() => setShowDetailsModal(false)}
          task={selectedTemplate}
          onRefresh={fetchTemplates}
        />
      )}

      {showSchedulerModal && (
        <TaskSchedulerModal
          show={showSchedulerModal}
          onHide={() => setShowSchedulerModal(false)}
          onSave={handleScheduleSave}
        />
      )}

    </Container>
  );
};

const TemplatesListPage = () => (
  <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} authenticationRequest={loginRequest}>
    <PageLayout2 title="Templates">
      <TemplatesListPageContent />
    </PageLayout2>
  </MsalAuthenticationTemplate>
);

export default TemplatesListPage;
